import { Dialog } from "primereact/dialog";
import React, { Component } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { MerchantAddresses } from "../../models/MerchantModel";
import { SelectItem } from "../../models/SelectItem";
import { toastService } from "../../services/toastService";
import { merchantAddressApiService } from "../../services/api/merchants/MerchantAddressApiService";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import Loader from "../../components/Loader";
import { Result } from "../../models/Result";
import { State } from "country-state-city";
import { AppConstants } from "../../models/AppConstants";
import ExtendedInputText from "../../components/ExtendedInputText";
import { Check } from "lucide-react";
import { countryCodeToFlag } from "../../utils/utils";

interface AddMerchantAddressProps {
    onHide: () => void;
    address: MerchantAddresses;
    types: SelectItem[];
    countries: SelectItem[];
    merchantId: number;
    localMode: boolean;
    onSubmit: (MerchantAddress: MerchantAddresses) => void;
}

interface AddMerchantAddressState {
    address: MerchantAddresses;
    isSaving: boolean;
    countryStates: SelectItem[];
    isSubmit: boolean;
}

export default class AddMerchantAddress extends Component<AddMerchantAddressProps, AddMerchantAddressState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    constructor(props: AddMerchantAddressProps | Readonly<AddMerchantAddressProps>) {
        super(props);

        this.state = {
            address: this.props.address,
            isSaving: false,
            countryStates: this.getStateProvinceCountries(props.address.countryId),
            isSubmit: false,
        };
    }

    updateValue = (fieldName: string, value: string) => {
        if ((fieldName.match("zipCode") && AppConstants.NUMARIC_ONLY_REGEX.test(value)) || value.length == 0) {
            this.saveFieldState(fieldName, value);
        } else if (fieldName.match("city") && AppConstants.CITY_REGEX.test(value)) {
            this.saveFieldState(fieldName, value);
        } else if (fieldName.match("addressLine") && value.length <= AppConstants.ADDRESS_LINE_LENGTH) {
            this.saveFieldState(fieldName, value);
        } else if (fieldName.match("addressType")) {
            this.saveFieldState(fieldName, value);
        }
    };

    saveFieldState = (fieldName: string, value: string) => {
        const userAddressDetails: any = this.state.address;
        userAddressDetails[fieldName] = value;
        this.setState({ address: userAddressDetails });
    };

    onSave = async () => {
        const emptyKeys = Object.keys(this.state.address).filter((key) => {
            const value = (this.state.address as any)[key];
            return value === null || value === "";
        });

        if (emptyKeys.length > 0) {
            this.setState({ isSubmit: true });
            return;
        }

        let result: Result<MerchantAddresses>;

        if (this.props.localMode) {
            result = { success: true, data: null!, message: "" } as Result<MerchantAddresses>;
        } else {
            result =
                this.state.address.id == 0
                    ? await merchantAddressApiService.create(this.state.address)
                    : await merchantAddressApiService.update(this.state.address);
        }

        this.setState({ isSaving: false });
        if (result.success) {
            this.props.onSubmit(this.state.address);
        } else {
            toastService.error(result.message == "Conflict" ? "Address already exists." : result.message);
        }
    };

    onCountryChange = (e: DropdownChangeEvent) => {
        const addressDetails = this.state.address;

        addressDetails.countryId = (e as DropdownChangeEvent).value;

        addressDetails.stateProvinceCounty = "";
        this.setState({ address: addressDetails, countryStates: this.getStateProvinceCountries(addressDetails.countryId) });
    };

    onStateProvinceCountyChange = (e: DropdownChangeEvent) => {
        const addressDetails = this.state.address;

        addressDetails.stateProvinceCounty = (e as DropdownChangeEvent).value;

        this.setState({ address: addressDetails });
    };

    selectedCountryTemplate = (option: SelectItem) => {
        if (option) {
            return (
                <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                    <span>{option.label}</span>
                </div>
            );
        }
        return <span className="text-neutral-400">Select a Country</span>;
    };

    countryOptionTemplate = (option: SelectItem) => {
        return (
            <div className="flex items-center gap-2">
                <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                <span>{option.label}</span>
            </div>
        );
    };

    validateInput = (fieldName: string): boolean => {
        const merchantDetails = this.state.address as any;
        return this.state.isSubmit && (merchantDetails[fieldName] === "" || undefined) ? true : false;
    };

    getStateProvinceCountries = (countryId: string) =>
        State.getStatesOfCountry(countryId).map((x) => ({ label: x.name, value: x.name }) as SelectItem);

    render() {
        if (this.state.address == null) {
            return <Loader />;
        }
        return (
            <Dialog header="Address" visible={true} style={{ width: "60vw" }} onHide={this.props.onHide}>
                <div className="space-y-5 p-2">
                    {/* Row 1: Address Line + City */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Address Line</label>
                            <ExtendedInputText
                                name="AddressLine"
                                value={this.state.address.addressLine}
                                required={this.validateInput("addressLine")}
                                onChange={(value) => this.updateValue("addressLine", value)}
                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("addressLine", value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">City</label>
                            <ExtendedInputText
                                name="City"
                                value={this.state.address.city}
                                required={this.validateInput("city")}
                                onChange={(value) => this.updateValue("city", value)}
                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("city", value)}
                            />
                        </div>
                    </div>

                    {/* Row 2: Country + Zip Code */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Country</label>
                            <ExtendedDropdown
                                style={{ width: "100%" }}
                                value={this.state.address.countryId}
                                options={this.props.countries}
                                onChange={this.onCountryChange}
                                filter
                                filterBy="label"
                                placeholder="Select a Country"
                                required={this.state.isSubmit && this.state.address.countryId === ""}
                                name="Country"
                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("country", value)}
                                valueTemplate={this.selectedCountryTemplate}
                                itemTemplate={this.countryOptionTemplate}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Zip Code</label>
                            <ExtendedInputText
                                name="ZipCode"
                                value={this.state.address.zipCode}
                                required={this.validateInput("zipCode")}
                                onChange={(value) => this.updateValue("zipCode", value)}
                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("zipCode", value)}
                            />
                        </div>
                    </div>

                    {/* Row 3: State/Province + Address Type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">State / Province / County</label>
                            <ExtendedDropdown
                                style={{ width: "100%" }}
                                value={this.state.address.stateProvinceCounty}
                                options={this.state.countryStates}
                                onChange={this.onStateProvinceCountyChange}
                                filter
                                filterBy="label"
                                placeholder="Select a State / Province"
                                required={this.state.isSubmit && this.state.address.stateProvinceCounty === ""}
                                name="StateProvinceCounty"
                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("stateProvinceCounty", value)}
                                disabled={this.state.address.countryId === ""}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Address Type</label>
                            <Dropdown
                                value={this.state.address.addressType}
                                options={this.props.types}
                                onChange={(e) => this.setState({ address: { ...this.state.address, addressType: e.value } })}
                                optionLabel="label"
                                placeholder="Select an Address Type"
                                className={`w-full ${this.state.isSubmit && this.state.address.addressType === "" ? "p-invalid" : ""}`}
                            />
                            {this.state.isSubmit && this.state.address.addressType === "" && (
                                <small className="text-danger-500 text-xs">Address Type is required.</small>
                            )}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end pt-2">
                        <Button label="Submit" icon={<Check size={16} />} loading={this.state.isSaving} onClick={this.onSave} />
                    </div>
                </div>
            </Dialog>
        );
    }
}
